import { useMemo, useState } from 'react';
import { Plus, Trash2, FileDown, Save, Layers } from 'lucide-react';
import api from '../lib/api';
import Button from './ui/Button';
import Input from './ui/Input';

export const RATE_CARD_PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'xiaohongshu', label: 'Xiaohongshu' },
  { value: 'wechat', label: 'WeChat' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'threads', label: 'Threads' },
];

export function normalizeRatePlatform(value: string): string {
  return (value || '').trim().toLowerCase();
}

function getPlatformLabel(value: string): string {
  const normalized = normalizeRatePlatform(value);
  return RATE_CARD_PLATFORMS.find((x) => x.value === normalized)?.label || value;
}

interface RateCardManagerProps {
  kolId: string;
  rateCards: Array<{
    id: string;
    platform: string;
    postRate?: number | null;
    videoRate?: number | null;
    storyRate?: number | null;
    currency?: string | null;
  }>;
  onReload: () => Promise<void> | void;
}

type EditableCard = {
  platform: string;
  postRate: string;
  videoRate: string;
  storyRate: string;
};

export default function RateCardManager({ kolId, rateCards, onReload }: RateCardManagerProps) {
  const [platform, setPlatform] = useState(RATE_CARD_PLATFORMS[0].value);
  const [rateType, setRateType] = useState<'postRate' | 'videoRate' | 'storyRate'>('postRate');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditableCard | null>(null);

  const sortedCards = useMemo(() => {
    return [...(rateCards || [])].sort((a, b) =>
      getPlatformLabel(a.platform).localeCompare(getPlatformLabel(b.platform))
    );
  }, [rateCards]);

  const duplicatePlatformCount = useMemo(() => {
    const count = new Map<string, number>();
    for (const rc of rateCards || []) {
      const key = normalizeRatePlatform(rc.platform);
      count.set(key, (count.get(key) || 0) + 1);
    }
    return Array.from(count.values()).filter((x) => x > 1).length;
  }, [rateCards]);

  const addRateCard = async () => {
    const parsed = parseInt(amount, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    try {
      setSubmitting(true);
      const payload: Record<string, string | number> = { platform };
      payload[rateType] = parsed;
      await api.post(`/kols/${kolId}/rate-cards`, payload);
      setAmount('');
      await onReload();
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (rc: NonNullable<RateCardManagerProps['rateCards']>[number]) => {
    setEditingId(rc.id);
    setEditDraft({
      platform: normalizeRatePlatform(rc.platform),
      postRate: rc.postRate == null ? '' : String(rc.postRate),
      videoRate: rc.videoRate == null ? '' : String(rc.videoRate),
      storyRate: rc.storyRate == null ? '' : String(rc.storyRate),
    });
  };

  const saveEdit = async () => {
    if (!editingId || !editDraft) return;
    const toNum = (v: string) => {
      if (v.trim() === '') return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      platform: editDraft.platform,
      postRate: toNum(editDraft.postRate),
      videoRate: toNum(editDraft.videoRate),
      storyRate: toNum(editDraft.storyRate),
    };
    await api.put(`/kols/${kolId}/rate-cards/${editingId}`, payload);
    setEditingId(null);
    setEditDraft(null);
    await onReload();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-glass bg-glass-2 p-3 md:grid-cols-4 md:p-4">
        <div>
          <label className="text-xs font-medium text-glass-text-2 block mb-1">Platform</label>
          <select
            className="min-h-11 w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40 md:min-h-0"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            {RATE_CARD_PLATFORMS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-glass-text-2 block mb-1">Rate type</label>
          <select
            className="min-h-11 w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40 md:min-h-0"
            value={rateType}
            onChange={(e) => setRateType(e.target.value as 'postRate' | 'videoRate' | 'storyRate')}
          >
            <option value="postRate">Post</option>
            <option value="videoRate">Video</option>
            <option value="storyRate">Story</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-glass-text-2 block mb-1">Amount (RM)</label>
          <Input
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 1200"
          />
        </div>
        <div className="flex items-end gap-2">
          <Button type="button" onClick={addRateCard} disabled={submitting || !amount} className="h-11 w-full md:h-auto">
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 w-11 shrink-0 p-0 md:h-auto md:w-auto md:px-3"
            aria-label="Download rate card PDF"
            onClick={async () => {
              try {
                const res = await api.get(`/kols/${kolId}/rate-cards.pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = 'rate-card.pdf';
                a.click();
                URL.revokeObjectURL(url);
              } catch {
                alert('PDF export failed');
              }
            }}
          >
            <FileDown className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 w-11 shrink-0 p-0 md:h-auto md:w-auto md:px-3"
            title="Merge duplicate platform rows"
            aria-label="Merge duplicate platform rows"
            onClick={async () => {
              const res = await api.post(`/kols/${kolId}/rate-cards/merge-duplicates`);
              const removed = res.data?.removedRows || 0;
              if (removed > 0) {
                alert(`Merged duplicates successfully. Removed ${removed} duplicate row(s).`);
              } else {
                alert('No duplicate platform rows found.');
              }
              await onReload();
            }}
          >
            <Layers className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {duplicatePlatformCount > 0 && (
        <div className="text-xs text-amber-500 dark:text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl px-3 py-2">
          Found duplicate rows on {duplicatePlatformCount} platform(s). Use the merge action to normalize historical data.
        </div>
      )}

      {sortedCards.length > 0 ? (
        <div className="border border-glass rounded-xl overflow-hidden">
          <div className="hidden grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3 border-b border-glass bg-glass-3 px-4 py-2 text-xs font-semibold text-glass-text-2 md:grid">
            <span>Platform</span>
            <span>Post (RM)</span>
            <span>Video (RM)</span>
            <span>Story (RM)</span>
            <span className="text-right">Action</span>
          </div>
          {sortedCards.map((rc) => {
            const isEditing = editingId === rc.id && editDraft;
            return (
            <div key={rc.id} className="grid grid-cols-2 items-end gap-x-3 gap-y-2 border-b border-glass px-3 py-3 text-sm last:border-b-0 hover:bg-glass-2 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] md:items-center md:px-4">
              <div className="col-span-2 min-w-0 md:col-span-1">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-glass-text-3 md:hidden">Platform</span>
                {isEditing ? (
                  <select
                    className="min-h-11 w-full rounded-lg border border-glass bg-glass-2 px-2 py-1 text-sm text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40 md:min-h-0"
                    value={editDraft.platform}
                    onChange={(e) => setEditDraft({ ...editDraft, platform: e.target.value })}
                  >
                    {RATE_CARD_PLATFORMS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="font-medium text-glass-text">{getPlatformLabel(rc.platform)}</span>
                )}
              </div>
              <div className="min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-glass-text-3 md:hidden">Post (RM)</span>
                {isEditing ? (
                  <Input type="number" min={0} value={editDraft.postRate} onChange={(e) => setEditDraft({ ...editDraft, postRate: e.target.value })} />
                ) : (
                  <span className="text-glass-text-2">{rc.postRate ?? '-'}</span>
                )}
              </div>
              <div className="min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-glass-text-3 md:hidden">Video (RM)</span>
                {isEditing ? (
                  <Input type="number" min={0} value={editDraft.videoRate} onChange={(e) => setEditDraft({ ...editDraft, videoRate: e.target.value })} />
                ) : (
                  <span className="text-glass-text-2">{rc.videoRate ?? '-'}</span>
                )}
              </div>
              <div className="min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-glass-text-3 md:hidden">Story (RM)</span>
                {isEditing ? (
                  <Input type="number" min={0} value={editDraft.storyRate} onChange={(e) => setEditDraft({ ...editDraft, storyRate: e.target.value })} />
                ) : (
                  <span className="text-glass-text-2">{rc.storyRate ?? '-'}</span>
                )}
              </div>
              <div className="col-span-2 flex justify-end md:col-span-1">
                <div className="grid w-full grid-cols-2 gap-2 md:flex md:w-auto">
                  {isEditing ? (
                    <>
                      <Button type="button" size="sm" className="h-11 md:h-auto" onClick={saveEdit}>
                        <Save className="w-4 h-4 mr-1" />
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-11 md:h-auto"
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button type="button" variant="outline" size="sm" className="h-11 md:h-auto" onClick={() => startEdit(rc)}>
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-11 border-rose-500/30 text-rose-500 hover:bg-rose-500/10 dark:text-rose-300 md:h-auto"
                        onClick={async () => {
                          await api.delete(`/kols/${kolId}/rate-cards/${rc.id}`);
                          await onReload();
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-glass-text-3">No rate card rows yet.</p>
      )}
    </div>
  );
}
